'use client';
import MuiBadge from '@mui/material/Badge';
import MuiIconButton from '@mui/material/IconButton';
import PropTypes from 'prop-types';
import React from 'react';
import { makeStyles } from 'tss-react/mui';

import idsTokensDefault from '../../utils/tokens/idsTokens';
import withRef from '../../utils/withRef';
import Badge from '../Badge';

export const styles = ({ idsTokens = idsTokensDefault }) => ({
  darkMode: {
    color: idsTokens.iconButton.onColor.color.icon.enabled,
    '&:hover': {
      color: idsTokens.iconButton.onColor.color.icon.hover,
      backgroundColor: idsTokens.iconButton.onColor.color.background.hover,
    },
    '&:disabled': {
      color: idsTokens.iconButton.onColor.color.icon.disabled,
    },
    '&:focus-visible': {
      color: idsTokens.button.onColor.color.discreet.content.focus,
      outline: `${idsTokens.functional.borderWidth.focus.default} solid ${idsTokens.functional.color.border.focusInverse}`,
      outlineOffset: '2px',
    },
  },
  danger: {
    color: idsTokens.iconButton.error.color.icon.enabled,
    backgroundColor: idsTokens.iconButton.error.color.background.enabled,

    '&:hover': {
      color: idsTokens.iconButton.error.color.icon.hover,
      backgroundColor: idsTokens.iconButton.error.color.background.hover,
    },

    '&:focus-visible': {
      color: idsTokens.iconButton.error.color.icon.focus,
      backgroundColor: idsTokens.iconButton.error.color.background.focus,
      outline: `${idsTokens.functional.borderWidth.focus.default} solid ${idsTokens.button.default.color.outline}`,
      outlineOffset: '2px',
    },

    '&:active': {
      color: idsTokens.iconButton.error.color.icon.pressed,
      backgroundColor: idsTokens.iconButton.error.color.background.pressed,
    },

    '&:disabled': {
      color: idsTokens.iconButton.error.color.icon.disabled,
      backgroundColor: idsTokens.iconButton.error.color.background.disabled,
    },
  },
  small: {
    width: idsTokens.iconButton.default.sizing.small.width,
    height: idsTokens.iconButton.default.sizing.small.height,
    padding: idsTokens.iconButton.default.padding.small,
  },
  standard: {
    width: idsTokens.iconButton.default.sizing.standard.width,
    height: idsTokens.iconButton.default.sizing.standard.height,
    padding: idsTokens.iconButton.default.padding.standard,
  },
  extraSmall: {
    width: idsTokens.iconButton.default.sizing.extraSmall.width,
    height: idsTokens.iconButton.default.sizing.extraSmall.height,
    padding: idsTokens.iconButton.default.padding.extraSmall,
  },
  open: {
    color: idsTokens.iconButton.default.color.icon.hover,
    backgroundColor: idsTokens.iconButton.default.color.background.hover,
  },
});

const useStyles = makeStyles()(styles);

export const IconButton = ({
  darkMode,
  destructiveAction,
  className,
  size = 'standard',
  open,
  children,
  forwardedRef: ref,
  ...rest
}) => {
  const { classes, cx } = useStyles();

  return (
    <MuiIconButton
      className={cx(
        darkMode && classes.darkMode,
        destructiveAction && classes.danger,
        (size === 'small' && classes.small) ||
          (size === 'extraSmall' && classes.extraSmall) ||
          (size === 'standard' && classes.standard) ||
          (size === 'medium' && classes.standard),
        open && classes.open,
        className
      )}
      {...rest}
      ref={ref}
      disableRipple
    >
      {React.cloneElement(
        children,
        children.type === Badge || children.type === MuiBadge
          ? {
              children: React.cloneElement(children.props.children, { fontSize: size }),
            }
          : { fontSize: size }
      )}
    </MuiIconButton>
  );
};

IconButton.propTypes = {
  /** Icon to display. */
  children: PropTypes.element.isRequired,
  /** If `true`, the component has a lighter appearance, for display on a dark background. */
  darkMode: PropTypes.bool,
  /** If `true`, IconButton will turn red on hover to indicate a destructive action. */
  destructiveAction: PropTypes.bool,
  /** If `true`, the component is disabled. */
  disabled: PropTypes.bool,
  /** Callback fired when the component is clicked. */
  onClick: PropTypes.func,
  /** The size of the component. */
  size: PropTypes.oneOf(['extraSmall', 'small', 'medium', 'standard']),
};

export const IconButtonWithoutTheme = withRef()(IconButton);

export default withRef()(IconButton);
